import React from 'react'
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import { tokens } from "../../../../theme";
import TextbookAndQuestions from './TextbookAndQuestions';
import Alert from '../../../../components/alert/Alert';
import Axios from 'axios';
import Loading from '../../../../components/Loading';
import BackdropComp from '../../../../components/Backdrop';
import { useSignOut } from "react-auth-kit";
import { useNavigate } from "react-router-dom";
import getCookies from '../../../../utils/getCookies';
import { Typography, LinearProgress, Chip, Grid } from '@mui/material';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { getAuthHeader } from '../../../../utils/authToken';
import { BINARY_GRADING_TYPES } from '../../../../utils/hwConstants';
import {
    countMeaningfulHomeworkAnswers,
    hasMeaningfulHomeworkAnswer,
} from './homeworkAnswerState';


const HWQuestionsAttempts = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const currentUser = getCookies("_auth_state");
    const userRole = currentUser ? currentUser.split(',')[2]?.split(':')[1]?.split('"')[1] : null;
    const userId = currentUser ? currentUser.split(',')[0]?.split(':')[1]?.split('"')[1] : null;
    const course_id = window.location.pathname.split("/")[window.location.pathname.split("/").length - 5];
    const homework_id = window.location.pathname.split("/")[window.location.pathname.split("/").length - 3];

    // for 401 error
    const signOut = useSignOut();
    const navigate = useNavigate();
    const handleLogout = () => {
        signOut();
        localStorage.removeItem("userName");
        localStorage.removeItem("who");
        navigate("/signin");
    }

    const [searchParams] = useSearchParams();
    const courseData = useOutletContext().courseData;
    const [loading, setLoading] = React.useState(true);

    // for backdrop
    const [backdropOpen, setBackdropOpen] = React.useState(false);
    const handleBackdropClose = () => {
        setBackdropOpen(false);
    };

    // for alert
    const [successAlertOpen, setSuccessAlertOpen] = React.useState(false);
    const [successAlertMessage, setSuccessAlertMessage] = React.useState("");
    const [errorAlertOpen, setErrorAlertOpen] = React.useState(false);
    const [errorAlertMessage, setErrorAlertMessage] = React.useState("");
    
    // for class materials
    const [classMaterial, setClassMaterial] = React.useState(null);
    const [hwQuestions, setHWQuestions] = React.useState([]);
    const [hwAnswers, setHWAnswers] = React.useState({});
    const [pages, setPages] = React.useState([]);
    const [pageFrom, setPageFrom] = React.useState(0);
    const [pageTo, setPageTo] = React.useState(0);
    const [submissionId, setSubmissionId] = React.useState(null);
    const [isSubmitted, setIsSubmitted] = React.useState(false);
    const [hwAnswerFiles, setHWAnswerFiles] = React.useState({});
    const [updatedHWAnswers, setUpdatedHWAnswers] = React.useState(false);
    const [studentName, setStudentName] = React.useState("");
    const [viewMode, setViewMode] = React.useState(searchParams.get('view') === 'student' ? 'student' : 'admin');
    const isStudentView = viewMode === 'student';

    let first_render = true;
  
    React.useEffect(() => {
        setLoading(true);
        const submission_id = searchParams.get("submission_id") || submissionId || null;
        const studentName = searchParams.get("student_name") || null;
        if (studentName) {
            setStudentName(studentName);
        }

        async function fetchClassMaterialAndHWQuestions() {
            // for student
            await Axios.get(`${process.env.REACT_APP_URL}/api/v1/courses/${course_id}/homework/${homework_id}/class-materials/hw-questions?submission_id=${submission_id}&user_uuid=${userId}&role=${userRole}`, {
                headers: {
                    Authorization: getAuthHeader(),
                },
            }).then((response) => {
                setClassMaterial(response.data.class_material);
                setHWQuestions(response.data.hw_questions);

                // Handle pages array or generate from page_from/page_to for backward compat
                if (response.data.pages && response.data.pages.length > 0) {
                    setPages(response.data.pages);
                } else if (response.data.page_from && response.data.page_to) {
                    const pagesFromRange = [];
                    for (let p = response.data.page_from; p <= response.data.page_to; p++) {
                        pagesFromRange.push(p);
                    }
                    setPages(pagesFromRange);
                }

                setPageFrom(response.data.page_from);
                setPageTo(response.data.page_to);
                setSubmissionId(response.data.submission_id);
                setIsSubmitted(response.data.is_submitted);

                // Mark results as viewed when student opens their submitted homework
                if (userRole === "student" && response.data.is_submitted && response.data.submission_id) {
                    Axios.get(`${process.env.REACT_APP_URL}/api/v1/courses/${course_id}/homeworks/${homework_id}/submissions/${response.data.submission_id}/result-opened`, {
                        headers: {
                            Authorization: getAuthHeader(),
                        },
                    }).catch((err) => {
                        console.log("Error marking result as viewed:", err);
                    });
                }

                for (let i = 0; i < response.data.hw_questions.length; i++) {
                    let hw_answer = response.data.hw_questions[i].hw_answers[0] || null;
                    if (response.data.hw_questions[i].question_type === "file_upload" && hw_answer) {
                        setHWAnswerFiles((prev) => ({
                            ...prev,
                            [response.data.hw_questions[i].id]: {
                                file: null,
                                prev_file: hw_answer.hw_answer_file ? hw_answer.hw_answer_file : null,
                            },
                        }));
                    }

                    // Convert answer from array to string for UI compatibility
                    // EXCEPT for tools that store answers as arrays (coordinate, table, mapping, numberline, polynomialGraph)
                    if (hw_answer && hw_answer.answer) {
                        if (Array.isArray(hw_answer.answer) && !['coordinate', 'table', 'mapping', 'numberline', 'polynomialGraph', 'rationalGraph', 'trigGraph', 'multiline'].includes(response.data.hw_questions[i].answer_tool)) {
                            hw_answer.answer = hw_answer.answer[0] || '';
                        }
                    }

                    setHWAnswers((prev) => ({
                        ...prev,
                        [response.data.hw_questions[i].id]: hw_answer,
                    }));
                }

                // console.log(response.data.class_material);
                // console.log(response.data.hw_questions);
            }).catch((error) => {
                if (error.response?.status === 401) {
                    handleLogout();
                    return;
                }
                setErrorAlertMessage(error.response?.data?.message || "Something went wrong");
                setErrorAlertOpen(true);
            });
        }

        async function fetchClassMaterialAndHWQuestionsForTeacherAndAdmin() {
            // for teacher and admin
            await Axios.get(`${process.env.REACT_APP_URL}/api/v1/courses/${course_id}/homework/${homework_id}/class-materials/hw-questions/for-instructors?user_uuid=${userId}&role=${userRole}`, {
                headers: {
                    Authorization: getAuthHeader(),
                },
            }).then((response) => {
                setClassMaterial(response.data.class_material);
                setHWQuestions(response.data.hw_questions);

                // Handle pages array or generate from page_from/page_to for backward compat
                if (response.data.pages && response.data.pages.length > 0) {
                    setPages(response.data.pages);
                } else if (response.data.page_from && response.data.page_to) {
                    const pagesFromRange = [];
                    for (let p = response.data.page_from; p <= response.data.page_to; p++) {
                        pagesFromRange.push(p);
                    }
                    setPages(pagesFromRange);
                }

                setPageFrom(response.data.page_from);
                setPageTo(response.data.page_to);
                setSubmissionId(response.data.submission_id);
                
                for (let i = 0; i < response.data.hw_questions.length; i++) {
                    let hw_answer = response.data.hw_questions[i].hw_answers[0] || null;
                    if (response.data.hw_questions[i].question_type === "file_upload" && hw_answer) {
                        setHWAnswerFiles((prev) => ({
                            ...prev,
                            [response.data.hw_questions[i].id]: {
                                file: null,
                                prev_file: hw_answer.hw_answer_file ? hw_answer.hw_answer_file : null,
                            },
                        }));
                    }

                    // Convert answer from array to string for UI compatibility
                    // EXCEPT for tools that store answers as arrays (coordinate, table, mapping, numberline, polynomialGraph)
                    if (hw_answer && hw_answer.answer) {
                        if (Array.isArray(hw_answer.answer) && !['coordinate', 'table', 'mapping', 'numberline', 'polynomialGraph', 'rationalGraph', 'trigGraph', 'multiline'].includes(response.data.hw_questions[i].answer_tool)) {
                            hw_answer.answer = hw_answer.answer[0] || '';
                        }
                    }

                    setHWAnswers((prev) => ({
                        ...prev,
                        [response.data.hw_questions[i].id]: hw_answer,
                    }));
                }

                // console.log(response.data.class_material);
                // console.log(response.data.hw_questions);
            }).catch((error) => {
                if (error.response?.status === 401) {
                    handleLogout();
                    return;
                }
                setErrorAlertMessage(error.response?.data?.message || "Something went wrong");
                setErrorAlertOpen(true);
            });
        }

        if (first_render) {
            if (userRole === "student" || submission_id) {
                fetchClassMaterialAndHWQuestions();
            } else {
                fetchClassMaterialAndHWQuestionsForTeacherAndAdmin();
            }
            first_render = false;
        }
        setUpdatedHWAnswers(false);
        setLoading(false);
    }, [courseData, updatedHWAnswers]);

    // Calculate progress
    const totalQuestions = hwQuestions.length;
    // Count only answers belonging to the questions currently shown. Structured
    // tools commonly persist placeholders such as [null] or {elements: []}; those
    // are UI state, not student answers.
    const answeredQuestions = countMeaningfulHomeworkAnswers(hwQuestions, hwAnswers, hwAnswerFiles);
    const progressPercentage = totalQuestions > 0 ? (answeredQuestions / totalQuestions) * 100 : 0;

    // Determine progress bar color
    const getProgressColor = () => {
        if (progressPercentage < 30) return 'error';
        if (progressPercentage < 70) return 'warning';
        return 'success';
    };

    // Calculate submission stats
    const answeredEntries = hwQuestions
        .map(question => ({ question, answer: hwAnswers[question.id] }))
        .filter(({ question, answer }) => hasMeaningfulHomeworkAnswer(
            question,
            answer,
            hwAnswerFiles[question.id]
        ));
    const autoGradedCorrect = answeredEntries.filter(({ answer }) => answer?.is_correct === true).length;
    const autoGradedIncorrect = answeredEntries.filter(({ answer }) => answer?.is_correct === false).length;
    const autoGradedTotal = autoGradedCorrect + autoGradedIncorrect;

    // Count feedback-based questions (long_answer without binary grading, file_upload)
    const feedbackQuestions = hwQuestions.filter(q =>
        q.question_type === 'file_upload' ||
        (q.question_type === 'long_answer' && !BINARY_GRADING_TYPES.includes(q.grading_type))
    );
    const feedbackGiven = feedbackQuestions.filter(q => {
        const answer = hwAnswers[q.id];
        return hasMeaningfulHomeworkAnswer(q, answer, hwAnswerFiles[q.id]) && answer?.feedback_json;
    }).length;
    const awaitingFeedback = feedbackQuestions.filter(q => {
        const answer = hwAnswers[q.id];
        const hasAnswer = hasMeaningfulHomeworkAnswer(q, answer, hwAnswerFiles[q.id]);
        return hasAnswer && !answer.feedback_json;
    }).length;

    // Pending is now only for auto-gradable questions that haven't been graded
    const pendingGrading = Math.max(0, answeredQuestions - autoGradedTotal - feedbackGiven - awaitingFeedback);
    const notAnswered = totalQuestions - answeredQuestions;
    const scorePercentage = autoGradedTotal > 0 ? (autoGradedCorrect / autoGradedTotal) * 100 : 0;

    return (
        <Box m="20px" marginX={3} marginY={3} minHeight="90%">
            { loading || courseData === null || classMaterial === null || submissionId === null ? 
                <Loading />
                : 
                <Box 
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        position: "relative",
                    }}
                >
                    <Box
                        position="absolute"
                        top="-50px"
                        left="0"
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                    >
                        <Typography variant="h4" sx={{fontWeight: "bold"}}>
                            HOMEWORK PROBLEMS {studentName && `FOR ${studentName.toUpperCase()}`}
                        </Typography>
                    </Box>
                    {userRole !== 'student' && (
                        <Box display="flex" justifyContent="flex-end" mb={1}>
                            <ToggleButtonGroup
                                value={viewMode}
                                exclusive
                                onChange={(e, newMode) => { if (newMode !== null) setViewMode(newMode); }}
                                size="small"
                            >
                                <ToggleButton value="admin">
                                    <AdminPanelSettingsIcon sx={{ mr: 0.5 }} fontSize="small" /> Admin View
                                </ToggleButton>
                                <ToggleButton value="student">
                                    <VisibilityIcon sx={{ mr: 0.5 }} fontSize="small" /> Student View
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                    )}

                    {/* Progress Bar or Stats - Show for students and teachers viewing student work */}
                    {((userRole === 'student' && !studentName) || studentName) && (
                        <>
                            {/* Before submission: Show progress bar */}
                            {!isSubmitted && (
                                <Box
                                    sx={{
                                        mb: 3,
                                        p: 2,
                                        backgroundColor: colors.primary[700],
                                        borderRadius: '8px',
                                        border: `2px solid ${colors.primary[500]}`,
                                    }}
                                >
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                        <Typography variant="h6" fontWeight="bold">
                                            {studentName ? `Student Progress: ${studentName}` : 'Progress'}
                                        </Typography>
                                        <Typography variant="h6" fontWeight="bold" color={getProgressColor() === 'success' ? colors.greenAccent[400] : getProgressColor() === 'warning' ? colors.yellowAccent[500] : colors.redAccent[400]}>
                                            {answeredQuestions} / {totalQuestions} answered ({Math.round(progressPercentage)}%)
                                        </Typography>
                                    </Box>
                                    <LinearProgress
                                        variant="determinate"
                                        value={progressPercentage}
                                        color={getProgressColor()}
                                        sx={{
                                            height: 10,
                                            borderRadius: 5,
                                            backgroundColor: colors.primary[500],
                                        }}
                                    />
                                </Box>
                            )}

                            {/* After submission: Show stats */}
                            {isSubmitted && (
                                <Box
                                    sx={{
                                        mb: 3,
                                        p: 3,
                                        backgroundColor: colors.primary[600],
                                        borderRadius: '8px',
                                        border: `2px solid ${colors.primary[300]}`,
                                    }}
                                >
                                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                                        <Typography variant="h5" fontWeight="bold">
                                            {studentName ? `Grading: ${studentName}` : 'Homework Submitted'}
                                        </Typography>
                                        <Chip
                                            label={studentName ? "✓ Submitted" : "✓ Complete"}
                                            color="success"
                                            sx={{ fontWeight: 'bold' }}
                                        />
                                    </Box>

                                    <Grid container spacing={2}>
                                        {/* Completion Stats */}
                                        <Grid 
                                            item 
                                            xs={12} 
                                            md={6}
                                        >
                                            <Box sx={{ p: 2, backgroundColor: colors.primary[750], borderRadius: '8px' }}>
                                                <Typography variant="subtitle2" color={colors.grey[300]} mb={1}>
                                                    Completion
                                                </Typography>
                                                <Typography variant="h4" fontWeight="bold" color={colors.greenAccent[400]}>
                                                    {answeredQuestions} / {totalQuestions}
                                                </Typography>
                                                <Typography variant="body2" color={colors.grey[400]}>
                                                    {Math.round(progressPercentage)}% answered
                                                </Typography>
                                            </Box>
                                        </Grid>

                                        {/* Auto-Graded Score (if any) */}
                                        {autoGradedTotal > 0 && (
                                            <Grid 
                                                item 
                                                xs={12} 
                                                md={6}
                                            >
                                                <Box sx={{ p: 2, backgroundColor: colors.primary[750], borderRadius: '8px' }}>
                                                    <Typography variant="subtitle2" color={colors.grey[300]} mb={1}>
                                                        Auto-Graded Score
                                                    </Typography>
                                                    <Typography variant="h4" fontWeight="bold" color={scorePercentage >= 70 ? colors.greenAccent[400] : scorePercentage >= 50 ? colors.yellowAccent[500] : colors.redAccent[400]}>
                                                        {autoGradedCorrect} / {autoGradedTotal}
                                                    </Typography>
                                                    <Typography variant="body2" color={colors.grey[400]}>
                                                        {Math.round(scorePercentage)}% correct
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        )}

                                        {/* Breakdown */}
                                        <Grid item xs={12}>
                                            <Box sx={{ p: 2, backgroundColor: colors.primary[750], borderRadius: '8px' }}>
                                                <Typography variant="subtitle2" color={colors.grey[300]} mb={2}>
                                                    Breakdown
                                                </Typography>
                                                <Grid container spacing={2}>
                                                    {autoGradedCorrect > 0 && (
                                                        <Grid item xs={6} sm={3}>
                                                            <Box display="flex" alignItems="center" gap={1}>
                                                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: colors.greenAccent[400] }} />
                                                                <Typography variant="body2">
                                                                    Correct: <strong>{autoGradedCorrect}</strong>
                                                                </Typography>
                                                            </Box>
                                                        </Grid>
                                                    )}
                                                    {autoGradedIncorrect > 0 && (
                                                        <Grid item xs={6} sm={3}>
                                                            <Box display="flex" alignItems="center" gap={1}>
                                                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: colors.redAccent[400] }} />
                                                                <Typography variant="body2">
                                                                    Incorrect: <strong>{autoGradedIncorrect}</strong>
                                                                </Typography>
                                                            </Box>
                                                        </Grid>
                                                    )}
                                                    {feedbackGiven > 0 && (
                                                        <Grid item xs={6} sm={3}>
                                                            <Box display="flex" alignItems="center" gap={1}>
                                                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: colors.purpleAccent ? colors.purpleAccent[400] : '#9c27b0' }} />
                                                                <Typography variant="body2">
                                                                    Feedback Given: <strong>{feedbackGiven}</strong>
                                                                </Typography>
                                                            </Box>
                                                        </Grid>
                                                    )}
                                                    {awaitingFeedback > 0 && (
                                                        <Grid item xs={6} sm={3}>
                                                            <Box display="flex" alignItems="center" gap={1}>
                                                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: colors.blueAccent[400] }} />
                                                                <Typography variant="body2">
                                                                    Awaiting Feedback: <strong>{awaitingFeedback}</strong>
                                                                </Typography>
                                                            </Box>
                                                        </Grid>
                                                    )}
                                                    {pendingGrading > 0 && (
                                                        <Grid item xs={6} sm={3}>
                                                            <Box display="flex" alignItems="center" gap={1}>
                                                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: colors.yellowAccent ? colors.yellowAccent[500] : '#ffc107' }} />
                                                                <Typography variant="body2">
                                                                    Pending: <strong>{pendingGrading}</strong>
                                                                </Typography>
                                                            </Box>
                                                        </Grid>
                                                    )}
                                                    {notAnswered > 0 && (
                                                        <Grid item xs={6} sm={3}>
                                                            <Box display="flex" alignItems="center" gap={1}>
                                                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: colors.grey[500] }} />
                                                                <Typography variant="body2">
                                                                    Not Answered: <strong>{notAnswered}</strong>
                                                                </Typography>
                                                            </Box>
                                                        </Grid>
                                                    )}
                                                </Grid>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Box>
                            )}
                        </>
                    )}

                    <Box
                        minWidth="20rem"
                        width="100%"
                        minHeight="70vh"
                        borderRadius="3px"
                        padding={3}
                        boxShadow="0px 0px 10px 10px rgba(0, 0, 0, 0.10)"
                        backgroundColor={colors.primary[600]}
                        display="flex"
                        flexDirection="column"
                        alignItems="left"
                        gap={3}
                        mb={3}
                    >
                        {classMaterial && (
                            <TextbookAndQuestions
                                PDF={classMaterial.file_url}
                                pages={pages}
                                pageFrom={pageFrom}
                                pageTo={pageTo}
                                hwQuestions={hwQuestions} 
                                setHWAnswers={setHWAnswers}
                                hwAnswers={hwAnswers}
                                userId={userId}
                                userRole={userRole}
                                course_id={course_id}
                                homework_id={homework_id}
                                colors={colors}
                                submissionId={submissionId}
                                isSubmitted={isSubmitted}
                                setHWAnswerFiles={setHWAnswerFiles}
                                hwAnswerFiles={hwAnswerFiles}
                                setSuccessAlertOpen={setSuccessAlertOpen}
                                setSuccessAlertMessage={setSuccessAlertMessage}
                                setErrorAlertOpen={setErrorAlertOpen}
                                setErrorAlertMessage={setErrorAlertMessage}
                                setBackdropOpen={setBackdropOpen}
                                handleLogout={handleLogout}
                                setUpdatedHWAnswers={setUpdatedHWAnswers}
                                isStudentView={isStudentView}
                            />
                        )}
                    </Box>
                    <Alert
                        successAlertOpen={successAlertOpen}
                        setSuccessAlertOpen={setSuccessAlertOpen}
                        errorAlertOpen={errorAlertOpen}
                        setErrorAlertOpen={setErrorAlertOpen}
                        successMsg={successAlertMessage}
                        errorMsg={errorAlertMessage}
                        vertical="bottom"
                        horizontal="left"
                    />
                    <BackdropComp backdropOpen={backdropOpen} handleBackdropClose={handleBackdropClose} />
                </Box>
            }
        </Box>
    )
}

export default HWQuestionsAttempts
